import { TaskType } from '@/shared/domain/taskTypes';
import { isAgenticAutonomyLevel } from '@/features/tasks/components/CreationWizard/businessGoals/autonomyLevels';

/** How many tasks the project summary card lists before "+N tasks". */
export const PROJECT_CARD_PREVIEW_TASK_LIMIT = 3;

/**
 * Whether a usecase is agentic (`autonomy_level` set and not "no tool
 * calling"). Agentic tasks show the robot badge on their `TaskTypeIcon`.
 */
export const isAgenticUsecase = (usecase) => isAgenticAutonomyLevel(usecase?.autonomy_level);

/**
 * Maps a usecase `task_type` (and `is_multilabel` for classification) to a
 * Spark `TaskTypeIcon` type — the colored tile shown on project and task
 * cards. Drafts created before a task type was chosen (or unrecognized
 * types) fall back to the "unknown" tile.
 */
export const getTaskTypeIconType = (taskType, isMultilabel = false) => {
    switch (taskType) {
        case TaskType.classification:
            return isMultilabel ? 'classification-multilabel' : 'classification';
        case TaskType.NER:
        case TaskType.NER_WITH_RELATIONS:
            return 'extraction';
        case TaskType.summarization:
            return 'summarization';
        case TaskType.contentGeneration:
            return 'content-generation';
        default:
            return 'unknown';
    }
};

const taskTitle = (subGroup) => {
    const usecases = subGroup?.usecases || [];
    return (
        (subGroup?.sub_group_name || '').trim() ||
        usecases[0]?.usecase_name ||
        'Task'
    );
};

const taskRecencyMs = (subGroup) => {
    let latest = 0;
    (subGroup?.usecases || []).forEach((usecase) => {
        const stamp = usecase?.last_update_time || usecase?.created_datetime;
        const ms = stamp ? new Date(stamp).getTime() : 0;
        if (Number.isFinite(ms) && ms > latest) latest = ms;
    });
    return latest;
};

/**
 * The tasks a project summary card should preview: the
 * `PROJECT_CARD_PREVIEW_TASK_LIMIT` most recently updated sub-groups
 * (falling back to created time, then list order), plus how many more
 * the project contains. There is no last-opened / last-visited field, so
 * recency is activity on the usecase documents, not "any user entered".
 */
export const getRecentProjectTasks = (app, limit = PROJECT_CARD_PREVIEW_TASK_LIMIT) => {
    const tasks = (app?.sub_groups || []).map((subGroup, index) => {
        const usecases = subGroup?.usecases || [];
        const firstUsecase = usecases[0];
        return {
            key: subGroup?.sub_group_name || firstUsecase?._id || `task-${index}`,
            name: taskTitle(subGroup),
            iconType: getTaskTypeIconType(firstUsecase?.task_type, firstUsecase?.is_multilabel),
            agentic: isAgenticUsecase(firstUsecase),
            recency: taskRecencyMs(subGroup),
            index,
            subGroup,
        };
    });

    tasks.sort((a, b) => b.recency - a.recency || b.index - a.index);

    return {
        preview: tasks.slice(0, limit).map(({ key, name, iconType, agentic, subGroup }) => ({
            key,
            name,
            iconType,
            agentic,
            subGroup,
        })),
        remainingCount: Math.max(0, tasks.length - limit),
        totalCount: tasks.length,
    };
};
